import { Project } from '../../types/project';
import { defaultEditionForState, getEditionPack } from './editions';

// Scope eligibility for the traditional (NEC 220.83 / 120.83) path.
//
// The section applies only to a dwelling unit served by an existing 3-wire
// 120/240 V or 208Y/120 V service or feeder, with NO minimum ampacity — a
// 60 A service qualifies. It does not apply to non-dwelling occupancies, to a
// whole multifamily building service, or to any three-phase service. These
// qualification conditions are identical across the 2020/2023/2026 packs
// (2026 keeps the 3-wire requirement and adds no ampacity minimum), so the
// rules below never branch on the edition. Only the SECTION NUMBERS in the
// messages are edition-dependent, and every one resolves through
// pack.sectionMap — never a literal — per the editions.ts header. The
// measured method (220.87) has none of these restrictions, so most
// ineligible projects are measured-path projects, and each refusal names
// that alternative rather than dead-ending.

export type IneligibleReason =
  | 'occupancy-unknown'
  | 'non-dwelling'
  | 'whole-building'
  | 'three-phase'
  | 'service-configuration';

export type EligibilityResult =
  | { eligible: true }
  | {
      eligible: false;
      reason: IneligibleReason;
      /** Plain-language sentence for the contractor. */
      message: string;
      /** Where this project goes instead. */
      alternative: string;
    };

/** "the standard method (Article 220 Part III)" — or just "the standard
 * method" when the pack's entry is null (2026, pending verification). */
function cite(label: string, ref: string | null): string {
  return ref ? `${label} (${ref})` : label;
}

export function checkTraditionalEligibility(project: Partial<Project>): EligibilityResult {
  const pack = getEditionPack(
    project.code_edition ?? defaultEditionForState(project.customer_state)
  );
  const dwelling = pack.sectionMap.existingDwelling;
  const measured = pack.sectionMap.determiningExisting;
  const standard = cite('the standard method', pack.sectionMap.standardMethodPart);
  const measuredAlternative = `Measured demand (NEC ${measured}) applies to any existing service — single or three phase, dwelling or not.`;

  const occupancy = project.occupancy_type ?? null;

  if (occupancy === null) {
    return {
      eligible: false,
      reason: 'occupancy-unknown',
      message: `Tell TrueLoad what this service feeds. The traditional calculation (NEC ${dwelling}) applies only to a dwelling unit.`,
      alternative: measuredAlternative,
    };
  }

  if (occupancy === 'non-dwelling') {
    return {
      eligible: false,
      reason: 'non-dwelling',
      message: `The optional dwelling methods (NEC ${dwelling}) apply only to dwelling units. A non-dwelling service is calculated under ${standard}, which TrueLoad does not implement.`,
      alternative: measuredAlternative,
    };
  }

  if (occupancy === 'multifamily-building') {
    const mfRef = pack.sectionMap.multifamilyOptional;
    const twoRef = pack.sectionMap.twoDwellingUnits;
    return {
      eligible: false,
      reason: 'whole-building',
      message: `NEC ${dwelling} calculates one dwelling unit, not a whole-building service. A whole-building service of three or more units is calculated under ${mfRef ? `NEC ${mfRef}` : 'the multifamily optional calculation'}, and two units under ${twoRef ? `NEC ${twoRef}` : 'the two-dwelling-unit provision'} — neither of which TrueLoad implements.`,
      alternative: `${measuredAlternative} Or evaluate a single unit's feeder by selecting the dwelling-unit occupancy.`,
    };
  }

  if (project.service_phase === 'three') {
    return {
      eligible: false,
      reason: 'three-phase',
      message: `The traditional calculation (NEC ${dwelling}) requires a 3-wire 120/240 V or 208Y/120 V service; a three-phase service falls to ${standard}.`,
      alternative: measuredAlternative,
    };
  }

  const volts = project.service_voltage_numeric;
  if (volts !== 240 && volts !== 208) {
    return {
      eligible: false,
      reason: 'service-configuration',
      message: `The traditional calculation (NEC ${dwelling}) requires a 3-wire 120/240 V or 208Y/120 V service; this service's configuration falls to ${standard}.`,
      alternative: measuredAlternative,
    };
  }

  // Deliberately no minimum ampacity: unlike the 100 A floor in the
  // new-dwelling optional method, this section has none — 60 A and 100 A
  // services qualify.
  return { eligible: true };
}
